import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

/**
 * WorkspaceContext - Manages workspace state globally across the application
 * Provides workspace data and functions for switching/validating workspaces
 */
const WorkspaceContext = createContext();

export const useWorkspace = () => {
  const context = useContext(WorkspaceContext);
  if (!context) {
    throw new Error('useWorkspace must be used within WorkspaceProvider');
  }
  return context;
};

export const WorkspaceProvider = ({ children }) => {
  const [currentWorkspace, setCurrentWorkspace] = useState(null);
  const [userWorkspaces, setUserWorkspaces] = useState([]);
  const [isWorkspaceLoading, setIsWorkspaceLoading] = useState(true);
  const [workspaceError, setWorkspaceError] = useState(null);

  // Load workspace from localStorage on mount
useEffect(() => {
  // Don't read from localStorage here — let the API set the workspace.
  // localStorage is written by selectWorkspace() for URL-based restores only.
  setIsWorkspaceLoading(false);
}, []);

  /**
   * Set the current workspace and save to localStorage
   */
  const selectWorkspace = useCallback((workspace) => {
    if (!workspace || !workspace.id) {
      console.error('Invalid workspace:', workspace);
      setWorkspaceError('Invalid workspace data');
      return;
    }

    try {
      setCurrentWorkspace(workspace);
      localStorage.setItem('selectedWorkspace', JSON.stringify(workspace));
      setWorkspaceError(null);
    } catch (error) {
      console.error('Error saving workspace to localStorage:', error);
      setWorkspaceError('Failed to save workspace selection');
    }
  }, []);

  /**
   * Clear current workspace from state and localStorage
   */
const clearWorkspace = useCallback(() => {
  setCurrentWorkspace(null);
  setUserWorkspaces([]);       // ← add this
  localStorage.removeItem('selectedWorkspace');
  localStorage.removeItem('selectedProject');
  setWorkspaceError(null);
}, []);

  /**
   * Get workspace ID from URL
   * Format: /{workspaceName_id}/dashboard/* or /{workspaceName_id}/project_{id}/dashboard/*
   */
  const getWorkspaceFromURL = useCallback((pathname) => {
    const match = pathname.match(/^\/([^/]+)_(\d+)(?:\/project_\d+)?\/dashboard/);
    if (match) {
      return {
        name: match[1],
        id: parseInt(match[2], 10),
      };
    }
    return null;
  }, []);

  /**
   * Format workspace path for URLs
   */
  const getWorkspacePath = useCallback((workspace) => {
    if (!workspace || !workspace.id || !workspace.workspace_name) {
      return null;
    }
    const workspaceName= workspace.workspace_name.trim()
    return `/workspace_${workspace.id}`;
  }, []);

  /**
   * Validate workspace exists in user's workspace list
   */
  const validateWorkspace = useCallback((workspace, userWorkspaceList = []) => {
    if (!workspace) return false;
    return userWorkspaceList.some(w => w.id === workspace.id);
  }, []);

  const value = {
    currentWorkspace,
    selectWorkspace,
    clearWorkspace,
    getWorkspaceFromURL,
    getWorkspacePath,
    validateWorkspace,
    isWorkspaceLoading,
    workspaceError,
    setWorkspaceError,
    userWorkspaces,
    setUserWorkspaces,
  };

  return (
    <WorkspaceContext.Provider value={value}>
      {children}
    </WorkspaceContext.Provider>
  );
};

export default WorkspaceContext;
